import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import { useNavigationData } from 'hooks/useNavigationData';

import { useSPMPlanContext } from './SPMPlanContext';

export const MAX_PREFETCH_ATTEMPTS = 2;

type SPMPrefetchTokenContextType = {
  getToken: (scenarioId?: string) => string | undefined;
  registerToken: (token: string, scenarioId?: string) => void;
  clearToken: (scenarioId?: string) => void;
  getTokenKey: (scenarioId?: string) => string | undefined;
  getPrefetchAttempts: (scenarioId?: string) => number;
  incrementPrefetchAttempts: (scenarioId?: string) => number;
  resetPrefetchAttempts: (scenarioId?: string) => void;
  clearRegistry: () => void;
};

const SPMPrefetchTokenContext = createContext<SPMPrefetchTokenContextType | undefined>(undefined);

export const useSPMPrefetchTokenRegistry = () => {
  const context = useContext(SPMPrefetchTokenContext);
  if (!context) {
    throw new Error('useSPMPrefetchTokenRegistry must be used within a SPMPrefetchTokenProvider');
  }
  return context;
};

type Props = {
  children: ReactNode;
};

export const SPMPrefetchTokenProvider = ({ children }: Props) => {
  // TODO: remove this map. not needed anymore but we're on a tight deadline
  const [tokens, setTokens] = useState<Record<string, string | undefined>>({});
  const { planningCycle, planMetricsWorksheet } = useSPMPlanContext();
  const { activeScenario } = useNavigationData();

  const _getTokenKey = useCallback(
    (scenarioId?: string) => {
      const effectiveScenario = scenarioId || activeScenario?.id;
      return `spm-${planningCycle?.id}:${planMetricsWorksheet?.id}:${effectiveScenario}`;
    },
    [planningCycle, planMetricsWorksheet, activeScenario],
  );

  // Track prefetch attempts per token key for retry limiting
  const [prefetchAttempts, setPrefetchAttempts] = useState<Map<string, number>>(new Map());

  const getToken = useCallback(
    (scenarioId?: string): string | undefined => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return undefined;
      }
      const tokenKey = _getTokenKey(scenarioId);
      return tokens[tokenKey];
    },
    [tokens, planningCycle, planMetricsWorksheet, activeScenario, _getTokenKey],
  );

  const registerToken = useCallback(
    (token: string, scenarioId?: string) => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return;
      }
      const tokenKey = _getTokenKey(scenarioId);
      setTokens((prev) => ({
        ...prev,
        [tokenKey]: token,
      }));
    },
    [planningCycle, planMetricsWorksheet, activeScenario, _getTokenKey],
  );

  const clearToken = useCallback(
    (scenarioId?: string) => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return;
      }
      const tokenKey = _getTokenKey(scenarioId);
      setTokens((prev) => ({
        ...prev,
        [tokenKey]: undefined,
      }));
    },
    [planningCycle, planMetricsWorksheet, activeScenario, _getTokenKey],
  );

  const getTokenKey = useCallback(
    (scenarioId?: string): string | undefined => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return undefined;
      }
      return _getTokenKey(scenarioId);
    },
    [planningCycle, planMetricsWorksheet, activeScenario, _getTokenKey],
  );

  const getPrefetchAttempts = useCallback(
    (scenarioId?: string): number => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return 0;
      }
      const tokenKey = _getTokenKey(scenarioId);
      return prefetchAttempts.get(tokenKey) || 0;
    },
    [planningCycle, planMetricsWorksheet, activeScenario, prefetchAttempts, _getTokenKey],
  );

  const incrementPrefetchAttempts = useCallback(
    (scenarioId?: string) => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return 0;
      }
      const tokenKey = _getTokenKey(scenarioId);
      const currentAttempts = prefetchAttempts.get(tokenKey) || 0;
      const newAttempts = currentAttempts + 1;
      setPrefetchAttempts((prev) => new Map(prev).set(tokenKey, newAttempts));
      return newAttempts;
    },
    [planningCycle, planMetricsWorksheet, activeScenario, prefetchAttempts, _getTokenKey],
  );

  const resetPrefetchAttempts = useCallback(
    (scenarioId?: string) => {
      const effectiveScenarioId = scenarioId || activeScenario?.id;
      if (!planMetricsWorksheet || !planningCycle || !effectiveScenarioId) {
        return;
      }
      const tokenKey = _getTokenKey(scenarioId);
      setPrefetchAttempts((prev) => {
        const newMap = new Map(prev);
        newMap.delete(tokenKey);
        return newMap;
      });
    },
    [planningCycle, planMetricsWorksheet, activeScenario, _getTokenKey],
  );

  const clearRegistry = useCallback(() => {
    clearToken();
    resetPrefetchAttempts();
  }, [clearToken, resetPrefetchAttempts]);

  return (
    <SPMPrefetchTokenContext.Provider
      value={{
        getToken,
        registerToken,
        clearToken,
        getTokenKey,
        getPrefetchAttempts,
        incrementPrefetchAttempts,
        resetPrefetchAttempts,
        clearRegistry,
      }}
    >
      {children}
    </SPMPrefetchTokenContext.Provider>
  );
};
